import { isTimeoutError } from "ky";
import { t } from "i18next";
import { getCurrentLanguage } from "@/core/utils/func/language-resolver";

/**
 * Forme commune aux erreurs axios (legacy) et ky HTTPError (nouveau).
 *
 * - axios met le corps parsé dans `error.response.data`
 * - ky 2.x met le corps parsé directement dans `error.data` (`error.response`
 *   reste l'objet `Response` natif, sans `.data`)
 *
 * Le handler lit les deux pour rester compatible.
 */
/** Corps backend possiblement retourné dans une erreur API. */
type ApiErrorBody = Record<string, unknown> & {
  message?: string | string[];
  /** Format d'erreur transverse Flash Pay (guide §4.8) : `{ "errorMsg": "…" }`. */
  errorMsg?: string;
  code?: string | number;
  errorCode?: string;
  statusCode?: number;
};

const isProxyTimeoutError = (body: ApiErrorBody | undefined): boolean =>
  body?.errorCode === "PROXY_TIMEOUT";

export interface IApiErrorType {
  message?: string;
  /** ky 2.x : corps parsé directement sur l'erreur */
  data?: ApiErrorBody;
  /** axios : corps parsé sous response.data */
  response?: {
    data?: ApiErrorBody;
    status?: number;
  };
}

/**
 * Messages ky / fetch bruts : URL, verbe HTTP, « Request failed… ».
 * Ils ne doivent jamais être affichés tels quels à l'utilisateur.
 */
export const looksLikeTechnicalClientMessage = (message: string): boolean => {
  const trimmed = message.trim();
  if (!trimmed) return true;
  if (/^Request failed\b/i.test(trimmed)) return true;
  if (/https?:\/\//i.test(trimmed)) return true;
  if (/\b(GET|POST|PUT|PATCH|DELETE|HEAD)\s+\S+/i.test(trimmed)) return true;
  if (/failed to fetch/i.test(trimmed)) return true;
  if (/network ?error/i.test(trimmed)) return true;
  if (/err_(internet|connection|name_not_resolved|timed_out|failed)/i.test(trimmed))
    return true;
  return false;
};

const isNetworkFailureMessage = (message: string): boolean =>
  /network ?error/i.test(message) ||
  /failed to fetch/i.test(message) ||
  /load failed/i.test(message) ||
  /err_(internet|connection|name_not_resolved|failed)/i.test(message);

const statusFromMessage = (message: string): number | undefined => {
  const match = message.match(/status code (\d{3})/i);
  if (!match) return undefined;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : undefined;
};

const messageForHttpStatus = (status: number): string => {
  if (status === 401) return t("unauthorizedError");
  if (status === 403) return t("forbiddenError");
  if (status === 404) return t("notFoundError");
  if (status === 408 || status === 504) return t("timeoutError");
  if (status === 409) return t("conflictError");
  if (status === 422) return t("validationError");
  if (status === 429) return t("rateLimitError");
  if (status >= 500) return t("serverError");
  return t("error");
};

/**
 * Extrait le corps complet retourné par le backend (statusCode, message, code, etc.).
 * Couvre ky (`error.data`) et axios (`error.response.data`).
 */
export const getBackendErrorBody = (
  error: unknown
): ApiErrorBody | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as IApiErrorType;
  return err.data ?? err.response?.data;
};

/**
 * Extrait le message backend de l'erreur si présent, sinon `undefined`.
 * Utile pour les `onError` qui veulent un fallback contextuel plutôt que la
 * valeur générique renvoyée par `apiErrorHandler`.
 */
export const getBackendErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as IApiErrorType;
  // `errorMsg` (format Flash Pay, guide §4.8) prime sur `message` (legacy).
  const errorMsg = err.data?.errorMsg ?? err.response?.data?.errorMsg;
  if (typeof errorMsg === "string" && errorMsg.trim()) {
    return looksLikeTechnicalClientMessage(errorMsg) ? undefined : errorMsg;
  }
  const msg = err.data?.message ?? err.response?.data?.message;
  if (typeof msg === "string") {
    return looksLikeTechnicalClientMessage(msg) ? undefined : msg;
  }
  if (Array.isArray(msg)) {
    const joined = msg.join("\n");
    return looksLikeTechnicalClientMessage(joined) ? undefined : joined;
  }
  // Certains endpoints renvoient un message multilingue `{ fr, en }` plutôt
  // qu'une chaîne — on résout selon la langue courante.
  if (msg && typeof msg === "object") {
    const localized = msg as Record<string, string>;
    const lang = getCurrentLanguage();
    const resolved = localized[lang] ?? localized.fr ?? localized.en;
    if (!resolved) return undefined;
    return looksLikeTechnicalClientMessage(resolved) ? undefined : resolved;
  }
  return undefined;
};

const fromClientMessage = (message: string): string => {
  if (message.startsWith("timeout")) return t("timeoutError");
  if (isNetworkFailureMessage(message)) return t("networkError");
  const status = statusFromMessage(message);
  if (status) return messageForHttpStatus(status);
  if (looksLikeTechnicalClientMessage(message)) return t("error");
  return message;
};

export const apiErrorHandler = (error: IApiErrorType | unknown): string => {
  if (!error) return "";
  if (typeof error === "string") return fromClientMessage(error);

  if (isTimeoutError(error)) return t("timeoutError");

  const backendBody = getBackendErrorBody(error);

  if (isProxyTimeoutError(backendBody)) return t("timeoutError");

  const backendMessage = getBackendErrorMessage(error);
  if (backendMessage) return backendMessage;

  const status = getHttpStatus(error);
  if (status) return messageForHttpStatus(status);

  const err = error as IApiErrorType;
  if (err.message) return fromClientMessage(err.message);

  return t("error");
};

/** Statut HTTP d'une erreur (ky `HTTPError` ou axios-like), si disponible. */
export const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") return undefined;
  const err = error as { response?: { status?: number } };
  return err.response?.status;
};

/**
 * 409 — même `Idempotency-Key`, traitement encore en cours (guide §4.7) :
 * attendre puis RELIRE la ressource, ne pas renvoyer la mutation.
 */
export const isIdempotencyInProgress = (error: unknown): boolean =>
  getHttpStatus(error) === 409;

/**
 * 422 — même `Idempotency-Key` réutilisée avec un corps différent
 * (guide §4.7) : régénérer la clé (`IdempotencyScope.renew()`) avant de
 * relancer — c'est une NOUVELLE tentative métier.
 */
export const isIdempotencyBodyMismatch = (error: unknown): boolean =>
  getHttpStatus(error) === 422;
